import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EntityCombobox, type EntityOption } from '@/components/shared/entity-combobox';
import { Listbox } from '@/components/ui/listbox';
import { api } from '@/lib/api';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { formatChronicleDate } from '@/utils/format';

export interface ReportGroupScope {
  type?: 'manual' | 'template';
  category?: 'group' | 'committee';
}

export interface ReportFilterDefinition {
  key: string;
  kind:
    | 'enum'
    | 'currency'
    | 'fund'
    | 'department'
    | 'unit'
    | 'group'
    | 'program'
    | 'member'
    | 'expenseCategory'
    | 'account'
    | 'chronicleEntry';
  labelKey: string;
  values?: string[];
  groupScope?: ReportGroupScope;
}

export const REPORT_FILTER_ANY = '';

interface ReportFiltersBarProps {
  filters: ReportFilterDefinition[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
}

interface MemberRow {
  id: string;
  firstName: string;
  lastName: string;
}

const memberLabel = (member: Pick<MemberRow, 'firstName' | 'lastName'>) =>
  `${member.firstName} ${member.lastName}`.trim();

interface ChronicleMomentRow {
  id: string;
  title: string;
  occurredOn: string;
  datePrecision: 'day' | 'month' | 'year';
  categoryName: string;
}

const chronicleMomentLabel = (moment: ChronicleMomentRow) =>
  `${formatChronicleDate(moment.occurredOn, moment.datePrecision)} · ${moment.title}`;

const ChronicleMomentFilter = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (key: string, value: string) => void;
}) => {
  const { t } = useTranslation('reports');
  const { t: tCommon } = useTranslation();
  const [query, setQuery] = useState('');

  const moments = useQuery({
    queryKey: ['content', 'chronicle', 'report-filter'],
    queryFn: () =>
      api.get<{ data: ChronicleMomentRow[] }>('/content/chronicle', {
        query: { page: 1, pageSize: 100 },
      }),
  });

  const selectedMoment = useQuery({
    queryKey: ['content', 'chronicle', 'report-filter-selected', value],
    queryFn: () => api.get<ChronicleMomentRow>(`/content/chronicle/${value}`),
    enabled: Boolean(value),
  });

  const options = useMemo((): EntityOption[] => {
    const needle = query.trim().toLowerCase();
    return (moments.data?.data ?? [])
      .filter((moment) => {
        if (!needle) return true;
        return (
          moment.title.toLowerCase().includes(needle) ||
          moment.categoryName.toLowerCase().includes(needle)
        );
      })
      .map((moment) => ({ id: moment.id, label: chronicleMomentLabel(moment) }));
  }, [moments.data?.data, query]);

  const selected = useMemo((): EntityOption | null => {
    if (!value) return null;
    const fromResults = options.find((option) => option.id === value);
    if (fromResults) return fromResults;
    if (selectedMoment.data) {
      return { id: selectedMoment.data.id, label: chronicleMomentLabel(selectedMoment.data) };
    }
    return null;
  }, [options, selectedMoment.data, value]);

  return (
    <div className="w-72 min-w-[16rem]">
      <EntityCombobox
        value={value || null}
        onChange={(next) => onChange('entryId', next ?? REPORT_FILTER_ANY)}
        options={options}
        selected={selected}
        onQueryChange={setQuery}
        loading={moments.isFetching}
        placeholder={t('chronicle.chooseMoment')}
        emptyMessage={tCommon('search.noResults')}
        clearLabel={t('chronicle.chooseMoment')}
      />
    </div>
  );
};

const MemberFilter = ({
  filterKey,
  value,
  onChange,
}: {
  filterKey: string;
  value: string;
  onChange: (key: string, value: string) => void;
}) => {
  const { t } = useTranslation('reports');
  const { t: tCommon } = useTranslation();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!value) setQuery('');
  }, [value]);

  const members = useQuery({
    queryKey: ['people', 'members', 'report-filter', filterKey, query],
    queryFn: () =>
      api.get<{ data: MemberRow[] }>(
        `/people/members?pageSize=20${query ? `&q=${encodeURIComponent(query)}` : ''}`,
      ),
    placeholderData: keepPreviousData,
  });

  const selectedMember = useQuery({
    queryKey: ['people', 'members', 'report-filter-selected', filterKey, value],
    queryFn: () => api.get<MemberRow>(`/people/members/${value}`),
    enabled: Boolean(value),
  });

  const options = useMemo(
    (): EntityOption[] =>
      (members.data?.data ?? []).map((member) => ({
        id: member.id,
        label: memberLabel(member),
      })),
    [members.data?.data],
  );

  const selected = useMemo((): EntityOption | null => {
    if (!value) return null;
    const fromResults = options.find((option) => option.id === value);
    if (fromResults) return fromResults;
    if (selectedMember.data) {
      return { id: selectedMember.data.id, label: memberLabel(selectedMember.data) };
    }
    return null;
  }, [options, selectedMember.data, value]);

  return (
    <div className="w-56 min-w-[12rem]">
      <EntityCombobox
        value={value || null}
        onChange={(next) => onChange(filterKey, next ?? REPORT_FILTER_ANY)}
        options={options}
        selected={selected}
        onQueryChange={setQuery}
        loading={members.isFetching}
        placeholder={t('filter.searchMember')}
        emptyMessage={tCommon('search.noResults')}
        clearLabel={t('filter.clearMember')}
      />
    </div>
  );
};

const SearchableEntityFilter = ({
  filterKey,
  value,
  label,
  anyLabel,
  searchPlaceholder,
  options,
  loading,
  onChange,
}: {
  filterKey: string;
  value: string;
  label: string;
  anyLabel: string;
  searchPlaceholder: string;
  options: Array<{ id: string; name: string }>;
  loading?: boolean;
  onChange: (key: string, value: string) => void;
}) => {
  const { t: tCommon } = useTranslation();

  const listOptions = useMemo(
    () => [
      { value: REPORT_FILTER_ANY, label: anyLabel },
      ...options.map((item) => ({ value: item.id, label: item.name })),
    ],
    [anyLabel, options],
  );

  return (
    <div className="w-48 min-w-[11rem]">
      <Listbox
        value={value || REPORT_FILTER_ANY}
        onChange={(next) => onChange(filterKey, next ?? REPORT_FILTER_ANY)}
        options={listOptions}
        searchable={listOptions.length > 6}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={tCommon('search.noResults')}
        disabled={loading}
        aria-label={label}
      />
    </div>
  );
};

export const ReportFiltersBar = ({ filters, values, onChange }: ReportFiltersBarProps) => {
  const { t } = useTranslation('reports');
  const church = useChurchStore((state) => state.church);
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);
  const membership = useCurrentMembership();
  const canViewGroups =
    isModuleEnabled('groups') && hasPermission(membership, PERMISSIONS.GROUPS_VIEW);
  const visibleFilters = filters.filter((filter) => {
    if (filter.kind === 'group' && !canViewGroups) return false;
    return true;
  });

  const handleChange = (key: string, value: string) => {
    if (key === 'departmentId' && value !== values.departmentId) {
      onChange('unitId', REPORT_FILTER_ANY);
    }
    if ((key === 'groupType' || key === 'category') && value !== values[key]) {
      onChange('groupId', REPORT_FILTER_ANY);
    }
    onChange(key, value);
  };

  const needsDepartments = filters.some((filter) => filter.kind === 'department');
  const needsUnits = filters.some((filter) => filter.kind === 'unit');
  const needsGroups = visibleFilters.some((filter) => filter.kind === 'group');
  const needsPrograms = filters.some((filter) => filter.kind === 'program');
  const needsCategories = filters.some((filter) => filter.kind === 'expenseCategory');
  const needsAccounts = filters.some((filter) => filter.kind === 'account');
  const needsFunds = filters.some((filter) => filter.kind === 'fund');

  const funds = useQuery({
    queryKey: ['giving', 'funds', 'report-filters'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>(
        '/giving/funds?pageSize=100&activeOnly=true',
      ),
    enabled: needsFunds,
  });

  const departments = useQuery({
    queryKey: ['people', 'departments', 'report-filters'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>('/people/departments?pageSize=100'),
    enabled: needsDepartments || needsUnits,
  });

  const units = useQuery({
    queryKey: ['people', 'department-units', 'report-filters', values.departmentId],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string; label: string }> }>(
        `/people/department-units?pageSize=100&departmentId=${encodeURIComponent(values.departmentId ?? '')}`,
      ),
    enabled: needsUnits && Boolean(values.departmentId),
  });

  const groupListScope = useMemo((): ReportGroupScope => {
    const groupFilter = filters.find((filter) => filter.kind === 'group');
    const scopedType = groupFilter?.groupScope?.type ?? values.groupType;
    const scopedCategory = groupFilter?.groupScope?.category ?? values.category;

    if (scopedType === 'template') {
      return { type: 'template', category: 'group' };
    }
    if (scopedCategory === 'committee') {
      return { type: 'manual', category: 'committee' };
    }
    if (scopedType === 'manual' || scopedCategory === 'group') {
      return { type: 'manual', category: 'group' };
    }
    return { type: 'manual', category: 'group' };
  }, [filters, values.category, values.groupType]);

  const groups = useQuery({
    queryKey: ['groups', 'report-filters', groupListScope.type, groupListScope.category],
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '100' });
      if (groupListScope.type) params.set('type', groupListScope.type);
      if (groupListScope.category) params.set('category', groupListScope.category);
      return api.get<{ data: Array<{ id: string; name: string }> }>(`/groups?${params.toString()}`);
    },
    enabled: needsGroups,
  });

  const programs = useQuery({
    queryKey: ['programs', 'report-filters'],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string }> }>('/programs?pageSize=100'),
    enabled: needsPrograms,
  });

  const accounts = useQuery({
    queryKey: ['financial-position', 'items', 'report-filters'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>('/financial-position/items'),
    enabled: needsAccounts,
  });

  const categories = useQuery({
    queryKey: ['expenses', 'categories', 'report-filters'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>('/expenses/categories'),
    enabled: needsCategories,
  });

  if (visibleFilters.length === 0) return null;

  const enumLabel = (filter: ReportFilterDefinition, option: string) => {
    if (filter.key === 'action') return t(`filterOption.action.${option}`, option);
    if (filter.key === 'entityType') return t(`filterOption.entityType.${option}`, option);
    if (filter.key === 'entryType') return t(`filterOption.entryType.${option}`, option);
    return t(`value.${option}`, option);
  };

  return (
    <>
      {visibleFilters.map((filter) => {
        const label = t(filter.labelKey);
        const value = values[filter.key] ?? REPORT_FILTER_ANY;
        const anyLabel =
          filter.key === 'visibility' ? t('filter.visibilityAny') : t('filter.any', { label });

        if (filter.kind === 'chronicleEntry') {
          return (
            <ChronicleMomentFilter
              key={filter.key}
              value={value}
              onChange={onChange}
            />
          );
        }

        if (filter.kind === 'member') {
          return (
            <MemberFilter
              key={filter.key}
              filterKey={filter.key}
              value={value}
              onChange={onChange}
            />
          );
        }

        if (filter.kind === 'fund') {
          return (
            <SearchableEntityFilter
              key={filter.key}
              filterKey={filter.key}
              value={value}
              label={label}
              anyLabel={anyLabel}
              searchPlaceholder={t('filter.searchFund')}
              options={funds.data?.data ?? []}
              loading={funds.isPending}
              onChange={handleChange}
            />
          );
        }

        if (filter.kind === 'department') {
          return (
            <SearchableEntityFilter
              key={filter.key}
              filterKey={filter.key}
              value={value}
              label={label}
              anyLabel={anyLabel}
              searchPlaceholder={t('filter.searchDepartment')}
              options={departments.data?.data ?? []}
              loading={departments.isPending}
              onChange={handleChange}
            />
          );
        }

        if (filter.kind === 'unit') {
          const departmentSelected = Boolean(values.departmentId);
          return (
            <SearchableEntityFilter
              key={filter.key}
              filterKey={filter.key}
              value={value}
              label={label}
              anyLabel={anyLabel}
              searchPlaceholder={
                departmentSelected
                  ? t('filter.searchUnit')
                  : t('filter.selectDepartmentFirst')
              }
              options={
                departmentSelected
                  ? (units.data?.data ?? []).map((unit) => ({
                      id: unit.id,
                      name: unit.label ?? unit.name,
                    }))
                  : []
              }
              loading={units.isPending}
              onChange={handleChange}
            />
          );
        }

        if (filter.kind === 'group') {
          const isCommittee =
            (filter.groupScope?.category ?? values.category) === 'committee';
          const isSmartGroup =
            (filter.groupScope?.type ?? values.groupType) === 'template';
          return (
            <SearchableEntityFilter
              key={filter.key}
              filterKey={filter.key}
              value={value}
              label={label}
              anyLabel={anyLabel}
              searchPlaceholder={
                isCommittee
                  ? t('filter.searchCommittee')
                  : isSmartGroup
                    ? t('filter.searchSmartGroup')
                    : t('filter.searchGroup')
              }
              options={groups.data?.data ?? []}
              loading={groups.isPending}
              onChange={handleChange}
            />
          );
        }

        if (filter.kind === 'program') {
          return (
            <SearchableEntityFilter
              key={filter.key}
              filterKey={filter.key}
              value={value}
              label={label}
              anyLabel={anyLabel}
              searchPlaceholder={t('filter.searchProgram')}
              options={programs.data?.data ?? []}
              loading={programs.isPending}
              onChange={onChange}
            />
          );
        }

        if (filter.kind === 'account') {
          return (
            <SearchableEntityFilter
              key={filter.key}
              filterKey={filter.key}
              value={value}
              label={label}
              anyLabel={anyLabel}
              searchPlaceholder={t('filter.searchAccount')}
              options={accounts.data?.data ?? []}
              loading={accounts.isPending}
              onChange={onChange}
            />
          );
        }

        if (filter.kind === 'expenseCategory') {
          return (
            <SearchableEntityFilter
              key={filter.key}
              filterKey={filter.key}
              value={value}
              label={label}
              anyLabel={anyLabel}
              searchPlaceholder={t('filter.searchCategory')}
              options={categories.data?.data ?? []}
              loading={categories.isPending}
              onChange={onChange}
            />
          );
        }

        if (filter.kind === 'currency') {
          const currencyOptions = church?.supportedCurrencies ?? [church?.baseCurrency ?? 'GHS'];
          return (
            <div key={filter.key} className="w-44 min-w-[10rem]">
              <Listbox
                value={value || REPORT_FILTER_ANY}
                onChange={(next) => onChange(filter.key, next ?? REPORT_FILTER_ANY)}
                options={[
                  { value: REPORT_FILTER_ANY, label: anyLabel },
                  ...currencyOptions.map((option) => ({
                    value: option,
                    label: option,
                  })),
                ]}
                aria-label={label}
              />
            </div>
          );
        }

        return (
          <div key={filter.key} className="w-44 min-w-[10rem]">
            <Listbox
              value={value || REPORT_FILTER_ANY}
              onChange={(next) => onChange(filter.key, next ?? REPORT_FILTER_ANY)}
              options={[
                { value: REPORT_FILTER_ANY, label: anyLabel },
                ...(filter.values ?? []).map((option) => ({
                  value: option,
                  label: enumLabel(filter, option),
                })),
              ]}
              aria-label={label}
            />
          </div>
        );
      })}
    </>
  );
};

export const activeReportFilterQuery = (
  filters: ReportFilterDefinition[],
  values: Record<string, string>,
): Record<string, string> => {
  const query: Record<string, string> = {};
  for (const filter of filters) {
    const value = values[filter.key];
    if (value) query[filter.key] = value;
  }
  return query;
};
